import React, { useState, useEffect } from 'react';
import { itinerariesService } from '../../services/itinerariesService';
import api from '../../services/api';
import './AddCommissionModal.css';

const AddCommissionModal = ({ isOpen, onClose, itinerary, onUpdate }) => {
  const [formData, setFormData] = useState({
    reservation_number: '',
    supplier: '',
    commission_amount: ''
  });
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (isOpen) {
      fetchSuppliers();
      // Load existing commission data if any
      if (itinerary.agent_only_info) {
        try {
          const agentInfo = typeof itinerary.agent_only_info === 'string' 
            ? JSON.parse(itinerary.agent_only_info) 
            : itinerary.agent_only_info;
          
          setFormData({
            reservation_number: agentInfo.reservation_number || itinerary.reservation_number || '',
            supplier: agentInfo.supplier || itinerary.supplier || '',
            commission_amount: agentInfo.commission_amount || itinerary.commission_amount || ''
          });
        } catch (e) {
          console.error('Error parsing agent_only_info:', e);
        }
      }
    }
  }, [isOpen, itinerary]);

  const fetchSuppliers = async () => {
    try {
      const response = await api.get('/suppliers?status=active');
      setSuppliers(response.data || []);
    } catch (error) {
      console.error('Error fetching suppliers:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      // Update the itinerary with commission info stored in agent_only_info
      const agentOnlyInfo = {
        reservation_number: formData.reservation_number,
        supplier: formData.supplier,
        commission_amount: parseFloat(formData.commission_amount) || 0
      };

      await itinerariesService.update(itinerary.id, {
        agent_only_info: agentOnlyInfo
      });

      onUpdate();
      onClose();
    } catch (err) {
      console.error('Error updating commission info:', err);
      setError('Failed to update commission information. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content add-commission-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Add Commission Information</h2>
          <button className="close-button" onClick={onClose}>×</button>
        </div>

        {error && <div className="error-message">{error}</div>}

        <form onSubmit={handleSubmit} className="commission-form">
          <div className="form-info">
            <p>This information will be available when submitting a commission request.</p>
          </div>

          <div className="form-group">
            <label>Reservation/Booking Number</label>
            <input
              type="text"
              value={formData.reservation_number}
              onChange={(e) => setFormData({ ...formData, reservation_number: e.target.value })}
              className="form-input"
              placeholder="Enter reservation or booking number"
            />
          </div>

          <div className="form-group">
            <label>Supplier</label>
            <select
              value={formData.supplier}
              onChange={(e) => setFormData({ ...formData, supplier: e.target.value })}
              className="form-input"
            >
              <option value="">Select a supplier</option>
              {suppliers.map(supplier => (
                <option key={supplier.id} value={supplier.name}>
                  {supplier.name}
                </option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label>Commission Amount</label>
            <div className="input-with-prefix">
              <span className="input-prefix">$</span>
              <input
                type="number"
                step="0.01"
                value={formData.commission_amount}
                onChange={(e) => setFormData({ ...formData, commission_amount: e.target.value })}
                className="form-input with-prefix"
                placeholder="0.00"
              />
            </div>
          </div>

          <div className="modal-actions">
            <button type="button" className="btn btn-secondary" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? 'Saving...' : 'Save Commission Info'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddCommissionModal;
